/**
 * BrandScope — render a subtree under a DIFFERENT brand than the app's.
 *
 * The active brand is a module-level singleton (`brand` in ./index), which is
 * right for the app: there is one tenant per session and one brand on screen.
 * The White-Label editor breaks that assumption. It shows a super admin what
 * ANOTHER tenant's pages look like, live, while the surrounding console stays
 * on the admin's own brand.
 *
 * Before this, each preview had to be handed the merged brand explicitly and
 * then thread it down by hand — which is why previews could paint the right
 * colours and still show the WRONG LOGO: any real component mounted inside
 * them called `useBrand()` and got the admin's brand back. Threading a prop
 * through every component a preview might mount is not a fix; the components
 * are the app's, and they are entitled to ask "what brand am I under?".
 *
 * So the question gets a scoped answer. `useBrand()` consults this context
 * first and falls back to the singleton, which means:
 *   • no call site changes,
 *   • every component inside a scope — Sidebar, MessageItem, BotAvatar,
 *     BotModal — paints the previewed tenant automatically,
 *   • outside a scope nothing changes at all, because the default is null.
 */

import { createContext, useContext } from 'react';
import type { BrandConfig } from './types';

/** null means "no scope — use the app's active brand". */
export const BrandScopeContext = createContext<BrandConfig | null>(null);

export function BrandScope({
  brand,
  children,
}: {
  brand: BrandConfig;
  children: React.ReactNode;
}) {
  return <BrandScopeContext.Provider value={brand}>{children}</BrandScopeContext.Provider>;
}

/**
 * The brand overriding this subtree, or null outside any scope.
 *
 * Use `useBrand()` for the answer you almost always want. This is for the rare
 * caller that needs to know whether it is INSIDE a preview — for example to
 * skip a fetch whose result would be the wrong tenant's anyway.
 */
export function useBrandScope(): BrandConfig | null {
  return useContext(BrandScopeContext);
}
